import { Injectable, inject } from '@angular/core';
import { calcInitials, getAnonymousGuestAvatar, getDefaultUserAvatar } from '../lib/create-avatar';
import { getAnonymousGuestName } from '../lib/get-anonymous-guest-name';
import { splitName } from '../lib/split-name';
import { StateService } from './state.service';

export type UserLike = {
  first_name?: string;
  last_name?: string;
  name?: string;
  avatar_url?: string;
  id?: string;
  anonymous_id?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  map: Map<string, string> = new Map();
  private state = inject(StateService);

  getDefaultUserAvatar(user: UserLike): string {
    const seed = user['anonymous_id'] || user['id'] || this.state.preGuestAnonymousId;
    const initials = calcInitials(user.first_name, user.last_name, user['email']);
    const cacheKey = `${seed}-${initials}`;
    if (this.map.has(cacheKey)) return this.map.get(cacheKey);
    const url = getDefaultUserAvatar(seed, initials);
    this.map.set(cacheKey, url);
    return url;
  }

  getAnonymousGuestAvatar(seed: string): string {
    const cacheKey = seed;
    if (this.map.has(cacheKey)) return this.map.get(cacheKey);
    const url = getAnonymousGuestAvatar(seed);
    this.map.set(cacheKey, url);
    return url;
  }

  private getAvatarInternal(user: UserLike): string {
    if (user?.avatar_url && !user.avatar_url.includes('img.clerk.com')) return user.avatar_url;

    if (!user) {
      if (!this.state.preGuestAnonymousId) return '';
      return this.getAnonymousGuestAvatar(this.state.preGuestAnonymousId);
    }

    if (user['anonymous_id']) {
      const anonymousName = getAnonymousGuestName(user['anonymous_id']);
      if (anonymousName === user['name']) {
        return this.getAnonymousGuestAvatar(user['anonymous_id']);
      }
    }

    return this.getDefaultUserAvatar(user);
  }

  public getAvatar(user: UserLike, tempName?: string): string {
    if (!user && tempName) {
      user = { name: tempName };
    }

    if ((user && !user.first_name && (user.name || tempName)) || tempName) {
      const { first_name: _first_name, last_name: _last_name } = splitName(tempName || user.name);
      user.first_name = _first_name;
      user.last_name = _last_name;
    }

    return this.getAvatarInternal(user);
  }
}
